import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import {
    EuiFlyout,
    EuiFlyoutHeader,
    EuiFlyoutBody,
    EuiButton,
    EuiForm,
    EuiTitle,
    EuiSpacer,
    EuiFlexGroup,
    EuiFlexItem,
    EuiButtonEmpty,
} from "@elastic/eui";
import _ from "lodash";
import AbstractDropdown from "../AbstractFormField/fields/AbstractDropdown";
import AbstractString from "../AbstractFormField/fields/AbstractString";
import AbstractNumber from "../AbstractFormField/fields/AbstractNumber";
import AbstractFile from "../AbstractFormField/fields/AbstractFile";
import AbstractSelect from "../AbstractFormField/fields/AbstractSelect";
import AbstractPassword from "../AbstractFormField/fields/AbstractPassword";
import AbstractPasswordConfirmation from "../AbstractFormField/fields/AbstractPasswordConfirmation";
import AbstractCheckbox from "../AbstractFormField/fields/AbstractCheckbox";
import CurrentLocation from "../CurrentLocation/container";

import AbstractDate from "../AbstractFormField/fields/AbstractDate";

const mapEntitiesToOptions = (entities, label) =>
    entities.map((entity) => ({
        label: _.startCase(entity[label]),
        value: entity.id,
    }));

export default function ({
    options,
    header,
    entityObject,
    includedEntities,
    onCancel,
    onSave,
    isLoading,
    hasErrors,
}) {
    const { register, errors, handleSubmit, setValue, watch, control } = useForm({
        mode: "onBlur",
    });

    const password = useRef({});
    password.current = watch("password", "");

    const onSubmit = (data) => {
        const { password, passwordConfirmation } = data;

        if (!(!!password && !!passwordConfirmation)) {
            onSave(_.omit(data, ["password", "passwordConfirmation"]));
        } else {
            onSave(data);
        }
    };

    useEffect(() => {
        options.formFields
            .filter((ff) => ff.hidden)
            .forEach((ff) => {
                register({ name: ff.name });
                setValue(ff.name, ff.value);
            });
    });

    useEffect(() => {
        // register form hooks for file fields or dropdowns
        options.formFields
            .filter((ff) => ff.type === "file" || ff.type === "list" || ff.type === "location")
            .forEach((ff) => {
                if (ff.type === "location") {
                    register({ name: "latitude" });
                    register({ name: "longitude" });
                    register({ name: "cityId" });
                }

                register({ name: ff.name });
                setValue(ff.name, entityObject[ff.name]);
            });
    }, [register, options.formFields, entityObject, setValue]);

    const handleFileUpload = (type, files) => {
        if (files.length === 0) return null;
        setValue(type, files[0].value);
    };

    const handleValueChange = (options, name, isMultiple) => {
        if (!options[0]) {
            setValue(name, null);
            return;
        }

        const value = isMultiple ? options.map((option) => +option.value) : options[0].value;
        setValue(name, value);
    };

    const onFetchLocation = (location, city) => {
        if (location.latitude && location.longitude) {
            setValue("latitude", location.latitude);
            setValue("longitude", location.longitude);
            setValue("cityId", city.id);
        } else {
            setValue("latitude", entityObject["latitude"]);
            setValue("longitude", entityObject["longitude"]);
            setValue("cityId", entityObject["cityId"]);
        }
    };

    return (
        <EuiFlyout
            maxWidth={600}
            ownFocus
            onClose={onCancel}
            aria-labelledby="flyoutLargeTitle"
        >
            <EuiFlyoutHeader hasBorder>
                <EuiTitle size="m">
                    <h2 id="flyoutLargeTitle">{header}</h2>
                </EuiTitle>
            </EuiFlyoutHeader>
            <EuiFlyoutBody>
                <EuiForm
                    component="form"
                    autoComplete="off"
                >
                    {options.formFields
                        .filter((ff) => !ff.hidden)
                        .map((formField) => {
                            switch (formField.type) {
                                case "number":
                                    return (
                                        <AbstractNumber
                                            options={formField}
                                            errors={errors}
                                            entityObject={entityObject}
                                            register={register}
                                        />
                                    );
                                case "file":
                                    return (
                                        <AbstractFile
                                            options={formField}
                                            errors={errors}
                                            onFileUpload={handleFileUpload}
                                        />
                                    );
                                case "list":
                                    return (
                                        <AbstractDropdown
                                            entityOptions={formField}
                                            errors={errors}
                                            onValueChange={(options, name) =>
                                                handleValueChange(
                                                    options,
                                                    name,
                                                    formField.isMultiple
                                                )
                                            }
                                            entityObject={entityObject}
                                            multipleSelection={formField.isMultiple}
                                            selectedOptions={
                                                formField.include &&
                                                    includedEntities &&
                                                    !_.isNil(includedEntities[formField.entityName]) &&
                                                    _.isArray(includedEntities[formField.entityName]) &&
                                                    !_.isNil(includedEntities[formField.entityName][0])
                                                    ? mapEntitiesToOptions(
                                                        includedEntities[formField.entityName],
                                                        formField.filterValue
                                                    )
                                                    : entityObject[formField.entityName] &&
                                                        entityObject[formField.name]
                                                        ? [
                                                            {
                                                                label: _.startCase(
                                                                    entityObject[formField.entityName]
                                                                ),
                                                                value: entityObject[formField.name],
                                                            },
                                                        ]
                                                        : undefined
                                            }
                                            isDisabled={formField.disabled}
                                        />
                                    );
                                case "select":
                                    return (
                                        <AbstractSelect
                                            options={formField}
                                            errors={errors}
                                            entityObject={entityObject}
                                            register={register}
                                        />
                                    );
                                case "checkbox":
                                    return (
                                        <AbstractCheckbox
                                            options={formField}
                                            isChecked={includedEntities.shouldNotify}
                                            errors={errors}
                                            register={register}
                                        />
                                    );
                                case "password":
                                    return (
                                        <AbstractPassword
                                            options={formField}
                                            errors={errors}
                                            entityObject={entityObject}
                                            register={register}
                                        />
                                    );
                                case "passwordConfirmation":
                                    return (
                                        <AbstractPasswordConfirmation
                                            options={formField}
                                            errors={errors}
                                            entityObject={entityObject}
                                            register={register}
                                            passwordValue={password}
                                        />
                                    );
                                case "location":
                                    return <CurrentLocation onFetchLocation={onFetchLocation} />;
                                case "date":
                                    return (
                                        <AbstractDate
                                            options={formField}
                                            errors={errors}
                                            entityObject={entityObject}
                                            control={control}
                                        />
                                    );
                                default:
                                    return (
                                        <AbstractString
                                            options={formField}
                                            errors={errors}
                                            entityObject={entityObject}
                                            register={register}
                                        />
                                    );
                            }
                        })}
                </EuiForm>
                <EuiSpacer size="xl" />
                <EuiFlexGroup justifyContent="flexEnd">
                    <EuiFlexItem grow={false}>
                        <EuiButtonEmpty onClick={onCancel}>Cancel</EuiButtonEmpty>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiButton
                            type="submit"
                            isLoading={isLoading}
                            fill
                            onClick={handleSubmit(onSubmit)}
                        >
                            Submit
                        </EuiButton>
                    </EuiFlexItem>
                </EuiFlexGroup>
            </EuiFlyoutBody>
        </EuiFlyout>
    );
}
